import { Injectable } from "@angular/core";
import { HttpBackend, HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { Announcement, Capability, FeatureKey, RoamingSettingKey } from "../model/shared.model";
import { OutlookService } from "./outlook/outlook.service";
import { MonitoringService } from "./monitoring.service";
import { SharedService } from "./shared.service";
import { UserInfoService } from "./userinfo.service";

export const VISIBLE_ANNOUNCEMENT_COUNT = 3;

@Injectable({
  providedIn: "root",
})
export class FeatureAnnouncementService {
  announcements: Announcement[] = [];

  private readIds: string[];
  private readonly httpClient: HttpClient;
  panelOpen: boolean = false;
  selectedAnnouncement: Announcement | null = null;
  constructor(
    private readonly outlookService: OutlookService,
    private readonly monitoringService: MonitoringService,
    private readonly sharedService: SharedService,
    private readonly userInfoService: UserInfoService,
    httpBackend: HttpBackend,
  ) {
    this.httpClient = new HttpClient(httpBackend);
    const stored = this.outlookService.roamingSettings.get(RoamingSettingKey.ReadAnnouncementIds);
    try {
      this.readIds = stored ? JSON.parse(stored) : [];
    } catch (e) {
      this.monitoringService.logFailed("ParseReadAnnouncementIds", e);
      this.readIds = [];
    }
  }

  private static readonly ANNOUNCEMENTS_URL = "assets/announcements.json";

  loadAnnouncements(): Promise<void> {
    return firstValueFrom(this.httpClient.get<Announcement[]>(FeatureAnnouncementService.ANNOUNCEMENTS_URL))
      .then(data => {
        // Release date decides the order, so where an entry sits in the file never hides it.
        this.announcements = data
          .filter((a, i) => this.isValidAnnouncement(a, i))
          .filter(a => this.hasKnownCapability(a))
          .filter(a => this.hasValidFeatures(a))
          .sort((a, b) => b.date.localeCompare(a.date));
      })
      .catch(err => {
        this.monitoringService.logFailed("LoadAnnouncements", err);
      });
  }

  private isValidAnnouncement(announcement: Announcement, index: number): boolean {
    if (!announcement.id || !announcement.title) {
      const identifier = announcement.id ? `id "${announcement.id}"` : announcement.title ? `title "${announcement.title}"` : `index ${index}`;
      this.monitoringService.logFailed("InvalidAnnouncement", new Error(`Announcement at ${identifier} is missing id or title`));
      return false;
    }
    if (typeof announcement.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(announcement.date)) {
      this.monitoringService.logFailed(
        "InvalidAnnouncementDate",
        new Error(`Announcement "${announcement.id}" has date "${announcement.date}", expected YYYY-MM-DD`),
      );
      return false;
    }
    for (const [name, url] of [
      ["learnMoreUrl", announcement.learnMoreUrl],
      ["noteUrl", announcement.noteUrl],
    ] as const) {
      if (url && !url.startsWith("https://")) {
        this.monitoringService.logFailed(
          "InvalidAnnouncementUrl",
          new Error(`Announcement "${announcement.id}" has non-https ${name} "${url}"`),
        );
        return false;
      }
    }
    if (!announcement.note && (announcement.noteHeading || announcement.noteUrl)) {
      this.monitoringService.logFailed(
        "InvalidAnnouncementNote",
        new Error(`Announcement "${announcement.id}" has noteHeading/noteUrl but no note`),
      );
      return false;
    }
    if (announcement.noteUrl && !announcement.noteLinkText) {
      this.monitoringService.logFailed(
        "InvalidAnnouncementNote",
        new Error(`Announcement "${announcement.id}" has noteUrl but no noteLinkText`),
      );
      return false;
    }
    return true;
  }

  private hasKnownCapability(announcement: Announcement): boolean {
    if (!announcement.requiredCapability || Object.values(Capability).includes(announcement.requiredCapability)) {
      return true;
    }
    this.monitoringService.logFailed(
      "UnknownAnnouncementCapability",
      new Error(`Announcement "${announcement.id}" has unknown requiredCapability "${announcement.requiredCapability}"`),
    );
    return false;
  }

  private hasValidFeatures(announcement: Announcement): boolean {
    const features = announcement.requiredFeatures;
    if (features === undefined) {
      return true;
    }
    const knownFeatures: string[] = Object.values(FeatureKey);
    if (Array.isArray(features) && features.length > 0 && features.every(f => knownFeatures.includes(f))) {
      return true;
    }
    this.monitoringService.logFailed(
      "InvalidAnnouncementFeatures",
      new Error(`Announcement "${announcement.id}" has empty or unknown requiredFeatures ${JSON.stringify(features)}`),
    );
    return false;
  }

  get visibleAnnouncements(): Announcement[] {
    return this.announcements
      .filter(a => !a.requiredCapability || this.sharedService.hasCapability(a.requiredCapability))
      .filter(a => !a.requiredFeatures || a.requiredFeatures.some(f => this.userInfoService.hasFeature(f)))
      .slice(0, VISIBLE_ANNOUNCEMENT_COUNT);
  }

  get activeAnnouncement(): Announcement | null {
    return this.selectedAnnouncement ?? this.latestUnreadAnnouncement;
  }

  get latestUnreadAnnouncement(): Announcement | null {
    return this.visibleAnnouncements.find(a => !this.readIds.includes(a.id)) ?? null;
  }

  hasUnread(): boolean {
    return this.visibleAnnouncements.some(a => !this.readIds.includes(a.id));
  }

  isUnread(id: string): boolean {
    return !this.readIds.includes(id);
  }

  togglePanel(): void {
    this.panelOpen = !this.panelOpen;
  }

  selectAnnouncement(announcement: Announcement): void {
    this.selectedAnnouncement = announcement;
    this.panelOpen = false;
  }

  markAsRead(id: string): void {
    if (!this.readIds.includes(id)) {
      this.readIds = [...this.readIds, id];
      this.outlookService.roamingSettings.set(RoamingSettingKey.ReadAnnouncementIds, JSON.stringify(this.readIds));
      this.outlookService.roamingSettings.saveAsync();
    }
    if (this.selectedAnnouncement?.id === id) {
      this.selectedAnnouncement = null;
    }
  }
}
